import moment from 'moment';
import { AppState } from '@/store';
import { ui } from '@/constants';
import round from 'lodash/round';
import { SelectableMenuItem, MenuItem } from '@/models';

const VICE_NOSCRIPT_WRAPPER_ID = 'vice-noscript-wrapper';

export type DeepPartial<T> = { [P in keyof T]?: T[P] extends Array<infer U> ? Array<DeepPartial<U>> : DeepPartial<T[P]> };

export interface UniqueValue<T> {
  value: T;
  key: any;
}

export function partial<T>(value: DeepPartial<T>): T {
  return value as T;
}

export function makeUniqueValue<T>(value: T): UniqueValue<T> {
  return {
    value,
    key: new Date().getTime(),
  };
}

export function isDefined<T>(item: T | undefined | null): item is T {
  return item !== undefined && item !== null;
}

export function prefixValues<T extends object>(obj: T, prefix: string): T {
  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      (newObj as any)[key] = prefix + (obj as any)[key];
    }
  }
  return newObj as T;
}

let NEXT_ID = 0;
export function nextId(): any {
  return ++NEXT_ID;
}

export function compareItems<TItem, TValue>(left: TItem, right: TItem, propertyFn: (t: TItem) => TValue): [TItem, TItem] {
  return propertyFn(left) > propertyFn(right) ? [left, right] : [right, left];
}

export function noop() {
  return;
}

export function freezeObject<T extends object>(val: T): T {
  return Object.freeze({ ...val });
}

export function freezeArray<T>(array: T[] | readonly T[]): readonly T[] {
  Object.freeze(array);

  for (const item of array) {
    Object.freeze(item);
  }

  return array;
}

export function capitalizeWordsInSentence(sentence: string): string {
  return sentence
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function stripNonNumerics(str: string): string {
  return str ? str.replace(/\D/g, '') : str;
}

export function stripNonNumericsKeepPeriod(str: string): string {
  return str ? str.replace(/[^0-9.]/g, '') : str;
}

export function stripLeadingOnesAndZeros(str: string): string {
  return str.replace(/^[01]+/, '');
}

export function chooseRandom<T>(x: ArrayLike<T>): ArrayLike<T>[number] {
  return x[Math.floor(Math.random() * x.length)];
}

export function generateRandomString(length: number): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_';
  let result = '';
  for (let i = length; i > 0; --i) {
    result += chooseRandom(chars);
  }
  return result;
}

export function generateRandomNumberString(length: number): string {
  const chars = '0123456789';
  let result = '';
  for (let i = length; i > 0; --i) {
    result += chooseRandom(chars);
  }
  return result;
}

export function makeSafariSafeDate(dateString: string): Date {
  return new Date(dateString.replace(' ', 'T'));
}

export async function sleep(ms: number): Promise<void> {
  await new Promise(res => setTimeout(res, ms));
}

// No real way to mock jasmine's clock with timezones yet
/* istanbul ignore next */
export function convertLocalToUTC(localTimestamp: string | undefined | null): string {
  if (!localTimestamp) {
    return '';
  }
  return moment(localTimestamp)
    .utcOffset(new Date().getTimezoneOffset())
    .toISOString();
}

export function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    script.src = url;
    script.type = 'application/javascript';

    // Extremely rare fallback to load directly into <head> if Vice <noscript> wrapper hasn't been initialized yet; basically a compiler pacifier.
    const wrapperEle = document.getElementById(VICE_NOSCRIPT_WRAPPER_ID) || document.head || document.getElementsByTagName('head')[0];
    wrapperEle.appendChild(script);
  });
}

// Creates a wrapper of <noscript data-vice-authority/> to stick third party CDN'd scripts into.
// If Vice goes down,
export function initViceNoscriptElement() {
  const head = document.head || document.getElementsByTagName('head')[0];
  const noscriptEle = document.createElement('noscript');
  noscriptEle.setAttribute('data-vice-authority', '');
  noscriptEle.id = VICE_NOSCRIPT_WRAPPER_ID;
  head.appendChild(noscriptEle);
}

// Default to desktop if state.ui is null or undefined
export function isDesktop(state: AppState): boolean {
  return state && state.ui ? state.ui.clientWidth > ui.breakpoints.tabletMax : true;
}

export function isLocal(): boolean {
  return process.env.VUE_APP_IS_LOCAL === 'true';
}

export function formatDecimal(value: number, decimalPoint: number = 2): number {
  return round(value, decimalPoint);
}

export function formatImageUrl(relativeUrl: string): string {
  return process.env.VUE_APP_CMS_BASEURL?.concat(relativeUrl) || '';
}

export function getVibesSmartLinkBaseUrl(): string {
  return process.env.VUE_APP_VIBES_SMARTLINK_BASE_URL || '';
}

// Returns e.g. 2020-01-01T00:00:00Z
export function getStartOfYear() {
  // prettier-ignore
  return moment().startOf('year').utc().format();
}

// Returns the default grouped menu item in the format that MenuItem can injest
export function getDefaultGroupedMenuItem(item: SelectableMenuItem<MenuItem, MenuItem>): Partial<MenuItem> | undefined {
  const defaultItemDetails = item.groupedItemSelectionOptions?.find(groupedItem => groupedItem.id === item.defaultItemId);
  if (!defaultItemDetails) {
    return;
  }
  return {
    itemId: defaultItemDetails.id,
    itemName: defaultItemDetails.name,
    unitPrice: defaultItemDetails.price,
    unitDeliveryPrice: defaultItemDetails.deliveryPrice,
    posId: defaultItemDetails.posId,
    countTowardsContentMax: defaultItemDetails.countTowardsContentMax,
    countTowardsCustomizationMax: defaultItemDetails.countTowardsCustomizationMax,
  };
}

export function getBrowserWidth(): number {
  return Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth
  );
}

// eslint-disable-next-line no-var
declare global {
  interface Window {
    targetPageParams: any;
    targetVm: any;
  }
}

window.targetVm = {}